import {
  ArrowUpRight,
  BadgeEuro,
  CalendarClock,
  ChevronRight,
  Handshake,
  Home,
  Instagram,
  Key,
  Languages,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  TrendingUp,
  UserPlus,
} from "lucide-react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Seo } from "../components/SiteSeo";
import { getAgent } from "../data/agents";
import { cardCopy } from "../data/cardCopy";
import { trackCardAction, trackContactClick } from "../lib/analytics";
import { buildVCard, downloadVCard } from "../lib/vcard";

const BRAND_LOGO =
  "https://customer-assets-agu9un31.emergentagent.net/job_estate-portal-143/artifacts/e0z5veoe_NEW%20LOGO%20PMP.png";

const VALUE_ICONS = [ShieldCheck, Handshake, BadgeEuro];

export default function AgentCardPage({ lang = "fr" }) {
  const { agentSlug } = useParams();
  const navigate = useNavigate();
  const base = getAgent(agentSlug);
  if (!base) return <Navigate to="/" replace />;

  const copy = cardCopy[lang];
  const agent = lang === "en" ? { ...base, ...base.en } : base;
  const fullName = `${agent.firstName} ${agent.lastName}`;
  const whatsappLink = (message) => `https://wa.me/${agent.whatsapp}?text=${encodeURIComponent(message)}`;
  const track = (action) => trackCardAction(action, `${agent.slug}_${lang}`);

  const handleSaveContact = () => {
    track("save_contact");
    downloadVCard(buildVCard(agent), `${agent.slug}.vcf`);
  };

  const handleEstimate = () => {
    track("estimate");
    if (agent.estimateUrl.startsWith("/")) {
      navigate(agent.estimateUrl);
      return;
    }
    window.open(agent.estimateUrl, "_blank", "noopener");
  };

  const bookingHref = agent.bookingUrl || whatsappLink(agent.whatsappMessages.booking);
  const switchTo = lang === "en" ? `/${agent.slug}` : `/en/${agent.slug}`;

  return (
    <main className="card-page" data-testid="agent-card-page" lang={lang}>
      <Seo title={copy.seoTitle(fullName)} description={copy.seoDescription(fullName, agent.area)} />
      <div className="card-shell">
        <header className="card-hero">
          <div className="card-hero-top">
            <Link to="/" className="card-brand" data-testid="card-brand-link" aria-label="Pimp My Place">
              <img src={BRAND_LOGO} alt="Pimp My Place" decoding="async" />
            </Link>
            <Link
              to={switchTo}
              className="card-lang-switch"
              onClick={() => track("language_switch")}
              data-testid="card-language-switch"
            >
              <Languages size={14} /> {copy.switchLabel}
            </Link>
          </div>
          <div className="card-identity">
            <div className="card-portrait" data-testid="card-portrait">
              {agent.photo ? (
                <img src={agent.photo} alt={fullName} decoding="async" />
              ) : (
                <div className="card-monogram" aria-hidden="true">
                  {agent.firstName[0]}
                  {agent.lastName[0]}
                </div>
              )}
            </div>
            <div>
              <h1 data-testid="card-agent-name">{fullName}</h1>
              <p className="card-role" data-testid="card-agent-role">{agent.role}</p>
            </div>
          </div>
          <p className="card-tagline" data-testid="card-tagline">
            {copy.taglineTop}
            <br />
            <span>{copy.taglineBottom}</span>
          </p>
          <span className="card-area" data-testid="card-area">
            <MapPin size={13} /> {agent.area}
          </span>
        </header>

        <section className="card-actions" aria-label={copy.projectEyebrow}>
          <a
            className="card-action card-action-whatsapp"
            href={whatsappLink(agent.whatsappMessages.general)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackContactClick("whatsapp", "agent_card");
              track("whatsapp");
            }}
            data-testid="card-whatsapp-button"
          >
            <span className="card-action-icon"><Phone size={19} /></span>
            <span>{copy.whatsapp}<small>{copy.whatsappHint}</small></span>
            <ChevronRight size={18} className="card-action-arrow" />
          </a>
          <a
            className="card-action card-action-primary"
            href={`tel:${agent.phone}`}
            onClick={() => {
              trackContactClick("phone", "agent_card");
              track("phone");
            }}
            data-testid="card-call-button"
          >
            <span className="card-action-icon"><Phone size={19} /></span>
            <span>{copy.call(agent.firstName)}<small>{agent.phoneDisplay}</small></span>
            <ChevronRight size={18} className="card-action-arrow" />
          </a>
          <a
            className="card-action"
            href={`mailto:${agent.email}`}
            onClick={() => {
              trackContactClick("email", "agent_card");
              track("email");
            }}
            data-testid="card-email-button"
          >
            <span className="card-action-icon"><Mail size={19} /></span>
            <span>{copy.email}<small>{agent.email}</small></span>
            <ChevronRight size={18} className="card-action-arrow" />
          </a>
          <a
            className="card-action"
            href={bookingHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("booking")}
            data-testid="card-booking-button"
          >
            <span className="card-action-icon"><CalendarClock size={19} /></span>
            <span>
              {agent.bookingUrl ? copy.booking : copy.bookingFallback}
              <small>{agent.bookingUrl ? copy.bookingHint : copy.bookingFallbackHint}</small>
            </span>
            <ChevronRight size={18} className="card-action-arrow" />
          </a>
          <button className="card-action" onClick={handleSaveContact} data-testid="card-save-contact-button">
            <span className="card-action-icon"><UserPlus size={19} /></span>
            <span>{copy.save}<small>{copy.saveHint(agent.firstName)}</small></span>
            <ChevronRight size={18} className="card-action-arrow" />
          </button>
          {agent.instagram && (
            <a
              className="card-action"
              href={agent.instagram}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("instagram")}
              data-testid="card-instagram-button"
            >
              <span className="card-action-icon"><Instagram size={19} /></span>
              <span>{copy.instagram}<small>{copy.instagramHint}</small></span>
              <ChevronRight size={18} className="card-action-arrow" />
            </a>
          )}
        </section>

        <section className="card-block" data-testid="card-project-block">
          <p className="card-eyebrow">{copy.projectEyebrow}</p>
          <h2 className="card-block-title">{copy.projectTitle}</h2>
          <p className="card-block-intro">{copy.projectIntro}</p>
          <div className="card-project">
            <article className="card-project-item">
              <h3><Home size={16} /> {copy.sell}</h3>
              <p>{copy.sellText}</p>
              <button className="card-project-cta" onClick={handleEstimate} data-testid="card-estimate-button">
                {copy.sellCta} <ArrowUpRight size={16} />
              </button>
            </article>
            <article className="card-project-item">
              <h3><Key size={16} /> {copy.buy}</h3>
              <p>{copy.buyText}</p>
              <a
                className="card-project-cta"
                href={whatsappLink(agent.whatsappMessages.buy)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("buy_whatsapp")}
                data-testid="card-buy-button"
              >
                {copy.buyCta} <ArrowUpRight size={16} />
              </a>
            </article>
            <article className="card-project-item card-project-secondary">
              <h3><TrendingUp size={16} /> {copy.invest}</h3>
              <p>{copy.investText}</p>
              <a
                className="card-project-cta"
                href={whatsappLink(agent.whatsappMessages.invest)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("invest_whatsapp")}
                data-testid="card-invest-button"
              >
                {copy.investCta} <ArrowUpRight size={16} />
              </a>
            </article>
          </div>
        </section>

        <section className="card-block" data-testid="card-values-block">
          <p className="card-eyebrow">{copy.valuesEyebrow}</p>
          <h2 className="card-block-title">{copy.valuesTitle}</h2>
          <div className="card-values">
            {copy.values.map(([title, text], index) => {
              const Icon = VALUE_ICONS[index];
              return (
                <article className="card-value" key={title}>
                  <Icon size={19} />
                  <div>
                    <strong>{title}</strong>
                    <p>{text}</p>
                  </div>
                </article>
              );
            })}
          </div>
          <div className="card-specialties" data-testid="card-specialties">
            {agent.specialties.map((specialty) => (
              <span key={specialty}>{specialty}</span>
            ))}
          </div>
        </section>

        <footer className="card-footer" data-testid="card-footer">
          <strong>Pimp My Place</strong>
          <p>
            {copy.footerBaseline}
            <br />
            {agent.website} · {agent.email}
          </p>
          <div className="card-footer-links">
            <Link to="/" data-testid="card-footer-site-link">{copy.footerSite}</Link>
            <Link to="/honoraires" data-testid="card-footer-fees-link">{copy.footerFees}</Link>
            <Link to="/confidentialite" data-testid="card-footer-privacy-link">{copy.footerPrivacy}</Link>
          </div>
          <small>
            Pimp My Place — {agent.role}. {copy.legal} {agent.website}.
          </small>
        </footer>
      </div>
    </main>
  );
}
